import { localizationService } from '@shared-services/localization-service';
import { CustomizationType } from '../types/EcomSDK';
import { Customization, CustomizationValue } from './types';

const { str } = localizationService;

const FILE_MAX_FILES = 5;

const getLength = (value?: CustomizationValue) =>
    (value as string[])?.length ?? 0;

const RULES = {
    requiredText: (value?: CustomizationValue) =>
        !!value || str('ui.runtimessr.productCustomizations.errors.required'),

    requiredFile: (value?: CustomizationValue) =>
        !!value?.length ||
        str('ui.runtimessr.productCustomizations.errors.required'),

    maxFiles: (value?: CustomizationValue) =>
        getLength(value) <= FILE_MAX_FILES ||
        str('ui.runtimessr.productCustomizations.errors.file.maxFiles', {
            max: FILE_MAX_FILES.toString(),
        }),

    requiredCheckbox: (value?: CustomizationValue) =>
        !!value?.length ||
        str('ui.runtimessr.productCustomizations.errors.checkbox.required'),

    minChoices: (min: number) => (value?: CustomizationValue) =>
        getLength(value) >= min ||
        str('ui.runtimessr.productCustomizations.errors.checkbox.minChoices', {
            min: min.toString(),
        }),
    maxChoices: (max: number) => (value?: CustomizationValue) =>
        getLength(value) <= max ||
        str('ui.runtimessr.productCustomizations.errors.checkbox.maxChoices', {
            max: max.toString(),
        }),
    exactlyChoices: (exactly: number) => (value?: CustomizationValue) => {
        const choicesLength = getLength(value);
        const isValid = choicesLength === exactly;

        return (
            isValid ||
            str(
                'ui.runtimessr.productCustomizations.errors.checkbox.exactlyChoices',
                { exact: exactly.toString() }
            )
        );
    },
};

export const buildValidationSchema = (customizations: Customization[]) =>
    customizations.reduce((acc, { type, id, mandatory, limitations }) => {
        if (!acc[id]) {
            acc[id] = [];
        }

        const targetValidation = acc[id];

        if (mandatory) {
            switch (type) {
                case CustomizationType.TEXT:
                    targetValidation.push(RULES.requiredText);
                    break;
                case CustomizationType.FILE:
                    targetValidation.push(RULES.requiredFile);
                    break;
                case CustomizationType.CHECKBOX:
                    targetValidation.push(RULES.requiredCheckbox);
                    break;
            }
        }

        if (type === CustomizationType.FILE) {
            targetValidation.push(RULES.maxFiles);
        }

        if (limitations) {
            const { min_choices, max_choices } = limitations;

            if (min_choices && max_choices && min_choices === max_choices) {
                targetValidation.push(RULES.exactlyChoices(min_choices));
            } else {
                if (min_choices) {
                    targetValidation.push(RULES.minChoices(min_choices));
                }
                if (max_choices) {
                    targetValidation.push(RULES.maxChoices(max_choices));
                }
            }
        }

        return acc;
    }, {} as Record<string, Array<(value: CustomizationValue) => boolean | string>>);
